// Library imports
import React, { useState } from "react";

// User Component imports
import { ProgressBar } from "_core/components/common/UI";
import OpsDialog from "../common/OpsDialog";
import { useVideoAttachments } from "./useVideoAttachments";
import { useSubmitWhenReady, calcOptimizeProgress } from "./useSubmitWhenReady";
import { VIDEO_SLOTS, isVoiceSelectable, voiceOptionLabel, willOverwrite } from "./appearanceDefs";
import VideoSlotRow from "./VideoSlotRow";

const NAME_MAX_LENGTH = 64;

// 未保存の入力を失わないよう、背景クリックでは閉じない
const IgnoreBackdropClick = () => {};

/**
 * 外見動画の編集（名前・ボイスの変更、動画の差し替え／追加、削除）
 */
const AppearanceEditModal = ({ appearance, voices, maxFileSize, progress, onClose, onSave, onDelete, onPreviewVoice }) => {
    const [name, setName] = useState(appearance.name);
    const [voiceId, setVoiceId] = useState(String(appearance.t_voice_type_id));
    const [deleteAsk, setDeleteAsk] = useState(false);
    const { files, attach, clear, clearAll, pick, setOptimize, autoMini, setAutoMini, busy, hasError } = useVideoAttachments();
    // 自分で添付したウィジェット表示動画があるときは、idle から作る切り替えを出さない
    const canAutoMini = files.idle?.canOptimize && !(files.idle_mini && !files.idle_mini.auto);
    const miniToggle = canAutoMini ? { checked: autoMini, onChange: setAutoMini } : null;
    const isSaving = progress !== null;

    // 現在のボイスが選択肢に無い（無効化済み等）場合も表示できるようにする（選べない）
    const voiceOptions = voices.some((v) => v.id === appearance.t_voice_type_id) ? voices
        : [{ id: appearance.t_voice_type_id, name: (appearance.voice_name || "-") + "（現在）", is_selectable: false }, ...voices];
    const currentVoice = voiceOptions.find((v) => v.id === appearance.t_voice_type_id);
    const voiceSelectable = isVoiceSelectable(voiceOptions.find((v) => String(v.id) === voiceId));

    const trimmedName = name.trim();
    const attached = Object.values(files);
    const hasBad = attached.some((f) => f.bad);
    const tooLarge = attached.some((f) => f.size > maxFileSize);
    const nameChanged = trimmedName !== appearance.name;
    const voiceChanged = String(appearance.t_voice_type_id) !== voiceId;
    // 名前・ボイスの更新はサーバーが選べないボイスを弾く。動画だけの変更は更新を呼ばないので保存できる
    const voiceBlocked = !voiceSelectable && (nameChanged || voiceChanged);
    const changed = nameChanged || voiceChanged || attached.length > 0;
    const canSave = !!(changed && trimmedName && trimmedName.length <= NAME_MAX_LENGTH && !voiceBlocked && !hasBad && !tooLarge && !hasError && !isSaving);
    const { reserved, request, cancel } = useSubmitWhenReady({
        busy, hasError, canSubmit: canSave,
        submit: () => onSave({ name: trimmedName, voiceId: Number(voiceId), files }),
    });
    const locked = isSaving || reserved;
    const waiting = calcOptimizeProgress(files);

    const footer = deleteAsk ? (
        <>
            <div className="ops-delete-confirm">
                <i className="bi bi-exclamation-triangle-fill" />
                <div>
                    <div className="ops-delete-confirm-title">この外見動画を削除しますか？</div>
                    <div className="ops-delete-confirm-note">※登録済みの動画もすべて削除され、元に戻せません。</div>
                    <div className="ops-delete-confirm-note">※設置先サイトのタグを差し替えていない場合は、この外見動画が使われている可能性があります。<br />　必ず削除前に設置先サイトを確認してください。</div>
                </div>
            </div>
            <button type="button" className="ops-footer-btn ops-delete-cancel" onClick={() => setDeleteAsk(false)}>やめる</button>
            <button type="button" className="ops-footer-btn is-danger" onClick={onDelete}><i className="bi bi-trash3" /><span>削除する</span></button>
        </>
    ) : (
        <>
            {/* 予約中・保存中は削除できないので出さず、進捗バーに幅を譲る */}
            {!locked &&
                <>
                    <button type="button" className="ops-delete-button" aria-disabled={appearance.is_use}
                        title={appearance.is_use ? "適用中の外見動画は削除できません。別の外見動画を適用してから削除してください" : "この外見動画を削除します（登録済みの動画もすべて削除されます）"}
                        onClick={() => !appearance.is_use && setDeleteAsk(true)}>
                        <i className="bi bi-trash3" /><span>外見動画を削除</span>
                    </button>
                    {appearance.is_use && <span className="ops-delete-note">※適用中の外見動画は削除できません</span>}
                    <div className="ops-footer-spacer" />
                </>
            }
            {locked &&
                <div className="ops-upload-progress">
                    <ProgressBar progress={isSaving ? progress : waiting.progress} subProgress={0} />
                    <span>
                        {!isSaving ? `最適化後に自動で保存（${waiting.done}/${waiting.total}）`
                            : progress < 1 ? `保存中 ${Math.round(progress * 100)}%` : "サーバーに保存中"}
                    </span>
                </div>
            }
            {!locked && <button type="button" className="ops-footer-btn" onClick={onClose}>キャンセル</button>}
            {reserved ?
                <button type="button" className="ops-footer-btn is-primary-outline" onClick={cancel}>自動保存を取り消す</button>
                :
                <button type="button" className="ops-footer-btn is-primary" disabled={!canSave} onClick={request}
                    title={busy ? "最適化が終わったら自動で保存します" : undefined}>
                    {busy ? "最適化後に保存" : "保存"}
                </button>
            }
        </>
    );

    return (
        <OpsDialog show={true} className="ops-scope" title="外見動画を編集" width={960} footer={footer}
            footerClassName={deleteAsk ? "is-delete-confirm" : ""}
            showClose={!locked} onClose={() => !locked && onClose()} onBackdropClick={IgnoreBackdropClick}>
            <div className="ops-form-card is-roomy is-edit">
                <div className="ops-edit-basic">
                <div className="ops-form-field">
                    <span className="ops-field-title">名前</span>
                    <input className="ops-input" value={name} maxLength={NAME_MAX_LENGTH} disabled={locked} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="ops-form-field">
                    <div className="ops-field-title-row">
                        <span className="ops-field-title">ボイス</span>
                        <span className="ops-field-sub">現在: {currentVoice ? `(ID:${currentVoice.id}) ${currentVoice.name}` : "-"}</span>
                    </div>
                    <div className="ops-voice-row">
                        <select className="ops-select" value={voiceId} disabled={locked} onChange={(e) => setVoiceId(e.target.value)}>
                            {voiceOptions.map((v) => <option key={v.id} value={v.id} disabled={!isVoiceSelectable(v)}>{voiceOptionLabel(v)}</option>)}
                        </select>
                        <button type="button" className="ops-voice-play" title="選択中のボイスをサンプルテキストで試聴します"
                            disabled={!voiceSelectable} onClick={() => onPreviewVoice(Number(voiceId))}><i className="bi bi-play-fill" /></button>
                    </div>
                    {!voiceSelectable && <div className="ops-warn-text"><i className="bi bi-exclamation-triangle-fill" />利用できないボイスです。名前を変えるには別のボイスを選んでください</div>}
                    {voiceChanged && <div className="ops-warn-text"><i className="bi bi-exclamation-triangle-fill" />保存すると、この外見動画の読み上げボイスが差し替わります</div>}
                </div>
                </div>
                <div className="ops-form-field">
                    <span className="ops-field-title">動画</span>
                    {appearance.is_use && <span className="ops-field-desc is-warn">適用中の外見動画です。保存するとウィジェットに即時反映されます。</span>}
                    {tooLarge && <div className="ops-error-text"><i className="bi bi-x-circle-fill" />1ファイル100MBまでです。ファイルを軽くしてください</div>}
                </div>
                <div className="ops-video-slots">
                    <div className="ops-edit-pending">
                        <span>{attached.length ? `${attached.length}件の動画を変更します。保存するまで反映されません。` : "変更したい動画の行にファイルをドラッグ＆ドロップするか、ボタンから選択してください。"}</span>
                        {attached.length > 0 && <button type="button" className="ops-edit-clear-all" onClick={clearAll} disabled={locked}>すべて取り消す</button>}
                    </div>
                    {VIDEO_SLOTS.map((slot) => (
                        <VideoSlotRow key={slot.key} mode="edit" slot={slot} current={appearance.assets[slot.key]?.file_name} entry={files[slot.key]}
                            overwrite={willOverwrite(appearance.assets[slot.key], files[slot.key])}
                            disabled={locked}
                            onPick={() => pick(slot.key)} onDrop={(file) => attach(slot.key, file)} onClear={() => clear(slot.key)}
                            onToggleOptimize={(optimize) => setOptimize(slot.key, optimize)}
                            miniToggle={slot.key === "idle" ? miniToggle : null} miniLabel="ウィジェット表示動画も idle から作り直す" />
                    ))}
                </div>
            </div>
        </OpsDialog>
    );
};

export default AppearanceEditModal;
